// Relationship registers — the four "relationship contexts" a user can
// tag a chat with. Used by ToneMirror Layers 2 / 3 / 4 to track rewrite
// behaviour, tone trends, and drift per register separately, so insights
// for "work" don't bleed into "family" and vice versa.
//
// ─────────────────────────────────────────────────────────────────────
// Two parallel naming layers (per spec):
//
//   • System names (internal — never shown to the user):
//       Authority · Peer · Familial · Intimate
//   • User-facing labels (always shown):
//       "Work contact" · "Friend" · "Family" · "Close partner"
//   • Storage keys (in chat.relationship_register and sessionLog —
//     unchanged from the original scheme so the intelligence layers
//     keep working without any modification):
//       work · friend · family · close
//
// The detection logic and the StorageKey ↔ SystemName ↔ UserLabel
// triplet are encoded once here. Everything else in the app reads one
// of these three projections via the helpers exported below.
// ─────────────────────────────────────────────────────────────────────

export const SYSTEM_NAMES = {
    AUTHORITY: "Authority",
    PEER: "Peer",
    FAMILIAL: "Familial",
    INTIMATE: "Intimate",
};

export const REGISTERS = [
    {
        // storage key — written to chat.relationship_register & sessionLog
        key: "work",
        // user-facing label
        label: "Work contact",
        // human-readable phrase (used inside register insight/trend lines)
        phrase: "work",
        // internal system name (per spec — NEVER surface in UI)
        systemName: SYSTEM_NAMES.AUTHORITY,
    },
    {
        key: "friend",
        label: "Friend",
        phrase: "friend",
        systemName: SYSTEM_NAMES.PEER,
    },
    {
        key: "family",
        label: "Family",
        phrase: "family",
        systemName: SYSTEM_NAMES.FAMILIAL,
    },
    {
        key: "close",
        label: "Close partner",
        phrase: "close relationship",
        systemName: SYSTEM_NAMES.INTIMATE,
    },
];

export const REGISTER_KEYS = REGISTERS.map((r) => r.key);

export const REGISTER_BY_KEY = REGISTERS.reduce((acc, r) => {
    acc[r.key] = r;
    return acc;
}, {});

const REGISTER_BY_SYSTEM_NAME = REGISTERS.reduce((acc, r) => {
    acc[r.systemName] = r;
    return acc;
}, {});

export function registerPhrase(key) {
    return REGISTER_BY_KEY[key]?.phrase || null;
}

export function registerLabel(key) {
    return REGISTER_BY_KEY[key]?.label || null;
}

export function isValidRegister(key) {
    return REGISTER_KEYS.includes(key);
}

// "Not set" copy used wherever the user-facing label needs a fallback.
export const REGISTER_LABEL_FALLBACK = "Not set";

// Convert system name → storage key. Used by detection results to
// translate the spec's category names into the storage layer.
function systemNameToKey(name) {
    return REGISTER_BY_SYSTEM_NAME[name]?.key || null;
}

// ─────────────────────────────────────────────────────────────────────
// Detection — Priority 1 → 4. First match wins. Stop.
// ─────────────────────────────────────────────────────────────────────
//
// All keyword matching is case-insensitive whole-word `contains()`. The
// keyword lists are exactly the spec's vocabulary; do NOT extend without
// updating the spec.

const KEYWORDS = {
    [SYSTEM_NAMES.AUTHORITY]: [
        "Manager",
        "Client",
        "Boss",
        "Senior",
        "HR",
        "Lead",
        "Director",
        "Executive",
        "Supervisor",
    ],
    [SYSTEM_NAMES.INTIMATE]: [
        "Partner",
        "Spouse",
        "Husband",
        "Wife",
        "Girlfriend",
        "Boyfriend",
    ],
    [SYSTEM_NAMES.FAMILIAL]: [
        "Mom",
        "Dad",
        "Mother",
        "Father",
        "Brother",
        "Sister",
        "Son",
        "Daughter",
        "Parent",
        "Parents",
        "Sibling",
        "Siblings",
        "In-law",
        "Uncle",
        "Aunt",
        "Grandma",
        "Grandpa",
        "Family",
        "Relative",
    ],
    [SYSTEM_NAMES.PEER]: [
        "Friend",
        "Friends",
        "Colleague",
        "Peer",
        "Team",
        "Coworker",
        "Teammate",
        "Buddy",
        "Mate",
    ],
};

// Priority 1 system-name evaluation order — must follow the spec's
// declared order (Authority → Intimate → Familial → Peer).
const PRIORITY_1_ORDER = [
    SYSTEM_NAMES.AUTHORITY,
    SYSTEM_NAMES.INTIMATE,
    SYSTEM_NAMES.FAMILIAL,
    SYSTEM_NAMES.PEER,
];

// Case-insensitive whole-word contains() — `\b` boundary so "team" matches
// "Team" / "team-lead" but not "teamster" via accidental substring hit.
// Tokens with internal hyphens (e.g. "In-law") use a non-word boundary
// fallback to keep the spec's vocabulary intact.
function containsKeyword(label, keyword) {
    if (!label || !keyword) return false;
    const safe = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const wordBoundary = /^[\w-]+$/.test(keyword)
        ? new RegExp(`(?:^|[^\\w])${safe}(?:$|[^\\w])`, "i")
        : new RegExp(safe, "i");
    return wordBoundary.test(label);
}

function detectByKeyword(label) {
    for (const sys of PRIORITY_1_ORDER) {
        for (const kw of KEYWORDS[sys]) {
            if (containsKeyword(label, kw)) {
                return { systemName: sys, matchedKeyword: kw };
            }
        }
    }
    return null;
}

// Priority 2 — hybrid name like "John-Manager" or "Radha HR". Split the
// label on a single non-word separator (hyphen, slash, dash, space,
// comma, dot) and re-run Priority 1 on the non-personal-name half.
function detectHybridName(label) {
    if (!label) return null;
    const parts = label.split(/[\s\-/,_.]+/).filter(Boolean);
    if (parts.length < 2) return null;
    for (const part of parts) {
        const hit = detectByKeyword(part);
        if (hit) return hit;
    }
    return null;
}

// Priority 3 — first-name heuristic. ALL must be true:
//   • no keyword hit anywhere in the label
//   • starts with a capital letter
//   • total length < 20 characters
function looksLikeFirstName(label) {
    if (!label) return false;
    const trimmed = label.trim();
    if (trimmed.length === 0 || trimmed.length >= 20) return false;
    if (!/^[A-Z]/.test(trimmed)) return false;
    return true;
}

/**
 * Run the spec's Priority 1 → 4 detection in order. First match wins.
 *
 * @param {string} label  Raw chat label as entered by the user.
 * @returns {{
 *   priority: 1 | 2 | 3 | 4,
 *   register: string | null,          // storage key (work|friend|family|close)
 *   systemName: string | null,        // internal — never shown to user
 *   confirmed: boolean,               // true when register is assigned silently
 *   isFirstName: boolean,             // Priority 3 only
 *   matchedKeyword: string | null,    // for tests / debug
 *   needsRegisterQuestion: boolean,   // UI should show Step 4 prompt
 * }}
 */
export function detectRegisterFromLabel(label) {
    const trimmed = (label || "").trim();
    if (!trimmed) {
        return {
            priority: 4,
            register: null,
            systemName: null,
            confirmed: false,
            isFirstName: false,
            matchedKeyword: null,
            needsRegisterQuestion: true,
        };
    }

    // Priority 1 — keyword on the whole label.
    const p1 = detectByKeyword(trimmed);
    if (p1) {
        return {
            priority: 1,
            register: systemNameToKey(p1.systemName),
            systemName: p1.systemName,
            confirmed: true,
            isFirstName: false,
            matchedKeyword: p1.matchedKeyword,
            needsRegisterQuestion: false,
        };
    }

    // Priority 2 — hybrid name (split on separators, keyword on parts).
    const p2 = detectHybridName(trimmed);
    if (p2) {
        return {
            priority: 2,
            register: systemNameToKey(p2.systemName),
            systemName: p2.systemName,
            confirmed: true,
            isFirstName: false,
            matchedKeyword: p2.matchedKeyword,
            needsRegisterQuestion: false,
        };
    }

    // Priority 3 — first name heuristic.
    if (looksLikeFirstName(trimmed)) {
        return {
            priority: 3,
            register: null,
            systemName: null,
            confirmed: false,
            isFirstName: true,
            matchedKeyword: null,
            needsRegisterQuestion: true,
        };
    }

    // Priority 4 — unrecognised.
    return {
        priority: 4,
        register: null,
        systemName: null,
        confirmed: false,
        isFirstName: false,
        matchedKeyword: null,
        needsRegisterQuestion: true,
    };
}

// Skip-rule helper for Step 4 — when the user dismisses the register
// question without tapping a button, the spec mandates defaulting to
// Peer (storage key: "friend").
export const SKIP_RULE_DEFAULT_REGISTER = "friend";

// Exposed for tests.
export const __internals = {
    KEYWORDS,
    PRIORITY_1_ORDER,
    containsKeyword,
    detectByKeyword,
    detectHybridName,
    looksLikeFirstName,
    systemNameToKey,
};
